/**
 * Main / Alt line filter. No React, no betMath — safe for Node ESM verify scripts.
 *
 * Freeze and some cache paths omit `is_main`. Requiring `is_main === true`
 * wiped every freeze prop; letting game lines always pass made Main/Alt look
 * like a no-op on mixed boards like "best nba bets".
 *
 * Rules:
 *   1. Explicit boolean stamp wins.
 *   2. Market/selection containing "alt" / "alternate" is alt.
 *   3. Otherwise the number with the most traditional books in that
 *      fixture+player+market group is main (Over/Under and both spread
 *      sides of the same number). Other numbers are alt.
 *   4. A group with only one number (moneylines, a single spread) is main,
 *      so Alt actually hides it.
 */

function stampedIsMain(option) {
    const top = option?.is_main;
    if (top === true || top === false) return top;
    const raw = option?.raw?.is_main;
    if (raw === true || raw === false) return raw;
    return null;
}

function optionLineGroup(option) {
    const r = option?.raw || {};
    const player = r.player_name || option?.player_name || '';
    const market = String(r.market || option?.title || '')
        .toLowerCase()
        .replace(/\balt(ernate)?s?\b/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    const fixture = r.fixture_id || option?.fixture_id || '';
    const line = r.selection_line ?? r.points ?? option?.selection_line ?? option?.points;
    let lineKey = '';
    if (line !== undefined && line !== null && line !== '') {
        const n = Number(line);
        lineKey = Number.isFinite(n) ? String(Math.abs(n)) : String(line);
    }
    return {
        group: `${fixture}|${player}|${market}`,
        line: lineKey,
    };
}

function optionBookCount(option) {
    const bo = option?.bookOdds || option?.raw?.bookOdds || {};
    const keys = Object.keys(bo);
    const trad = keys.filter((k) => {
        const s = String(k).toLowerCase();
        return !(s.includes('kalshi') || s.includes('polymarket') || s.includes('exchange')
            || s.includes('sporttrade') || s.includes('prediction'));
    }).length;
    if (trad > 0) return trad;
    if (keys.length > 0) return keys.length;
    return option?.availableBooks?.length || option?.raw?._sportsbook_count || 1;
}

export function optionPassesLineTypeFilter(option, filterLineType, pool = []) {
    if (!filterLineType || filterLineType === 'all') return true;

    const stamped = stampedIsMain(option);
    if (stamped === true) return filterLineType === 'main';
    if (stamped === false) return filterLineType === 'alt';

    const blob = `${option?.raw?.market || option?.title || ''} ${option?.raw?.selection || option?.description || ''}`.toLowerCase();
    if (/\balt(ernate)?s?\b/.test(blob)) return filterLineType === 'alt';

    const self = optionLineGroup(option);
    const lineScores = new Map();
    for (const o of pool) {
        const id = optionLineGroup(o);
        if (id.group !== self.group) continue;
        const otherStamp = stampedIsMain(o);
        if (otherStamp === true) {
            lineScores.set(id.line, Number.POSITIVE_INFINITY);
            continue;
        }
        if (otherStamp === false) continue;
        lineScores.set(id.line, Math.max(lineScores.get(id.line) || 0, optionBookCount(o)));
    }
    let bestLine = self.line;
    let bestScore = -1;
    for (const [line, score] of lineScores) {
        if (score > bestScore) {
            bestScore = score;
            bestLine = line;
        }
    }
    const isMain = self.line === bestLine;
    return filterLineType === 'main' ? isMain : !isMain;
}

/** Pre-line-type pool → chip counts. Main and Alt are a partition of `all`. */
export function countLineTypeSplits(pool = []) {
    let main = 0;
    let alt = 0;
    for (const o of pool) {
        if (optionPassesLineTypeFilter(o, 'main', pool)) main++;
        else alt++;
    }
    return { all: pool.length, main, alt };
}
